import { useState, useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useVoiceAssistantState } from "@/contexts/voice-assistant-state";
import { Calendar, CalendarDays, ChevronLeft, ChevronRight, MapPin, Clock, Loader2, Pencil, X, Plus, Trash2, Lock, Users, Globe, BedDouble, AlertTriangle, ChevronsRight, ChevronsLeft, ExternalLink, Sparkles, RefreshCw, UserPlus, Mail, CheckCircle2, XCircle, Video, Check, Target, CalendarClock, Wand2, FileText, ListChecks, StopCircle } from "lucide-react";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { CoachAspirationsPanel } from "@/components/coach/coach-aspirations-panel";
import type { PersonalCalendarBlock, CalendarBlockLayer, CalendarBlockKind, CalendarBlockVisibility, LodgingStay, LodgingSuggestion, Conflict } from "@shared/schema";


interface ScheduleEntry {
  id: string;
  year: number;
  date: string;
  status: string | null;
  city: string | null;
  state: string | null;
  regionId: string | null;
  optionB: string | null;
  notes: string | null;
  driveTime: string | null;
  leadPages: number | null;
  totalBookingAmount: number | null;
  estDays: number | null;
  region?: { id: string; name: string; color: string } | null;
}

interface GridData {
  year: number;
  month: number;
  entries: Record<string, ScheduleEntry[]>;
}

interface TeammateBlock {
  id: string;
  startAt: string;
  endAt: string;
  title: string;
  kind: string;
  layer: string;
  visibility: string;
  isBusy: boolean;
}

interface TeammateGridData extends GridData {
  sharingMode: "shared" | "availability_only";
  blocks: Record<string, TeammateBlock[]>;
  targetUser: { id: string; firstName: string; lastName: string };
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

function getStatusColor(status: string | null) {
  switch (status?.toLowerCase()) {
    case "confirmed":
    case "booked":
      return "bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30";
    case "hold":
    case "option":
      return "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-500/30";
    case "cancelled":
      return "bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/30";
    case "travel":
      return "bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

// Color a calendar-grid block chip. Coach-created aspiration blocks are
// colored by their completion lifecycle so the user sees green(confirmed) /
// yellow(assumed) progress right on the calendar; planned coach "water"
// blocks are violet, manual blocks stay violet, and busy/missed are muted.
function getBlockChipClass(block: PersonalCalendarBlock) {
  if (block.isBusy) {
    return "bg-muted/40 text-muted-foreground border-muted/60";
  }
  if ((block as any).isCoachCreated) {
    switch ((block as any).coachCompletion) {
      case "confirmed":
        return "bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/40";
      case "assumed":
        return "bg-yellow-500/20 text-yellow-700 dark:text-yellow-500 border-yellow-500/40";
      case "missed":
        return "bg-muted/40 text-muted-foreground border-muted/60 line-through";
      default:
        return "bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200 border-violet-200 dark:border-violet-800";
    }
  }
  return "bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200 border-violet-200 dark:border-violet-800";
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function KPICard({ title, value, subtitle, icon: Icon, color }: { 
  title: string; 
  value: string | number; 
  subtitle?: string;
  icon: typeof Calendar;
  color: string;
}) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={cn("w-12 h-12 rounded-full flex items-center justify-center", color)}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProgressRing({ progress, size = 80, strokeWidth = 8, color = "stroke-primary" }: {
  progress: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill="none"
        className="text-muted/30"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className={color}
      />
    </svg>
  );
}


// ============================================================================
// Google Calendar View — monthly grid showing real GCal events + master
// schedule ribbons. Double-click a day → DayDetailView. Green + → add event.
// ============================================================================

interface GCalEvent {
  id: string;
  summary: string;
  start: { dateTime?: string; date?: string };
  end: { dateTime?: string; date?: string };
  colorId?: string;
  description?: string;
  location?: string;
  attendees?: Array<{ email: string; displayName?: string; responseStatus?: string }>;
}

function NewEventDialog({ defaultDate, onClose }: { defaultDate: string; onClose: () => void }) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(defaultDate || new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("11:00");
  const [allDay, setAllDay] = useState(false);
  const [notes, setNotes] = useState("");
  const [inviteeInput, setInviteeInput] = useState("");
  const [invitees, setInvitees] = useState<string[]>([]);
  const [eventType, setEventType] = useState<"appointment" | "meeting" | "stay">("appointment");
  const [addMeet, setAddMeet] = useState(false);
  const [orgPickerOpen, setOrgPickerOpen] = useState(false);
  // Task #405 — Google Meet transcription controls.
  const [meetingType, setMeetingType] = useState<"internal" | "client">("internal");
  const [transcribeRequested, setTranscribeRequested] = useState(false);
  const [consentAcknowledged, setConsentAcknowledged] = useState(false);

  const { data: calStatus } = useQuery<{ connected: boolean; email: string | null; meetScopesGranted?: boolean }>({
    queryKey: ["/api/user/calendar/status"],
  });

  // Task #405 — workspace transcription policy (readable by any authed user).
  const { data: meetPolicy } = useQuery<{
    meetTranscriptionEnabled?: boolean;
    meetTranscribeInternalAuto?: boolean;
    meetTranscribeClientAllowed?: boolean;
  }>({
    queryKey: ["/api/calendar/settings"],
  });

  const transcriptionPolicyOn = !!meetPolicy?.meetTranscriptionEnabled;
  const canTranscribeThis = transcriptionPolicyOn && (
    meetingType === "internal" ? (meetPolicy?.meetTranscribeInternalAuto ?? true) : (meetPolicy?.meetTranscribeClientAllowed ?? true)
  );
  const meetScopesGranted = !!calStatus?.meetScopesGranted;

  // Default the transcription toggle: internal meetings auto-on (when allowed),
  // client meetings opt-in (default off). Re-evaluates when type/policy changes.
  useEffect(() => {
    if (!addMeet || !canTranscribeThis || !meetScopesGranted) {
      setTranscribeRequested(false);
      return;
    }
    setTranscribeRequested(meetingType === "internal" ? (meetPolicy?.meetTranscribeInternalAuto ?? true) : false);
  }, [addMeet, meetingType, canTranscribeThis, meetScopesGranted, meetPolicy?.meetTranscribeInternalAuto]);

  const connectMeetTranscription = async () => {
    try {
      const res = await apiRequest("GET", "/api/user/calendar/auth?meet=1");
      const data = await res.json();
      if (data?.authUrl) window.location.href = data.authUrl;
    } catch (err: any) {
      toast({ title: "Couldn't start Meet connection", description: err?.message, variant: "destructive" });
    }
  };

  const { data: orgMembers = [] } = useQuery<
    { id: string; email: string; firstName: string; lastName: string; role: string }[]
  >({
    queryKey: ["/api/calendar/members"],
  });

  const submit = useMutation({
    mutationFn: async () => {
      const startAt = allDay
        ? new Date(date + 'T00:00:00').toISOString()
        : new Date(date + 'T' + startTime + ':00').toISOString();
      const endAt = allDay
        ? new Date(date + 'T23:59:00').toISOString()
        : new Date(date + 'T' + endTime + ':00').toISOString();
      return apiRequest("POST", "/api/user/calendar/events", {
        title: title.trim(),
        startAt,
        endAt,
        allDay,
        notes: notes.trim() || null,
        attendees: invitees,
        eventType,
        addMeet: eventType === "meeting" && addMeet && !!calStatus?.connected,
        meetingType,
        transcribeRequested: eventType === "meeting" && addMeet && transcribeRequested && canTranscribeThis && meetScopesGranted,
        consentAcknowledged,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/calendar/events"] });
      toast({ title: "Event added to your Google Calendar" });
      onClose();
    },
    onError: (err: any) => {
      toast({ title: "Couldn't add the event", description: err?.message, variant: "destructive" });
    },
  });

  const addInvitee = () => {
    const email = inviteeInput.trim();
    if (email && email.includes('@') && !invitees.includes(email)) {
      setInvitees(prev => [...prev, email]);
      setInviteeInput("");
    }
  };

  const invalid = !title.trim() || !date || (!allDay && (!startTime || !endTime || endTime <= startTime));

  return (
    <DialogContent className="sm:max-w-md" data-testid="dialog-new-event">
      <DialogHeader>
        <DialogTitle>New calendar event</DialogTitle>
        <DialogDescription>Add an appointment, meeting, or stay to your Google Calendar.</DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {!calStatus?.connected && (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-2 text-xs text-amber-800 dark:text-amber-200">
            Google Calendar isn't connected — this event won't sync until you connect it in Settings.
          </div>
        )}

        <div>
          <Label className="mb-1.5 block">Type</Label>
          <div className="flex gap-2">
            {(["appointment", "meeting", "stay"] as const).map(type => (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setEventType(type);
                  if (type !== "meeting") setAddMeet(false);
                }}
                className={cn(
                  "flex-1 py-1.5 px-2 rounded-md border text-sm capitalize transition-colors",
                  eventType === type
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted/40 text-muted-foreground border-border hover:bg-muted"
                )}
                data-testid={`button-event-type-${type}`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {eventType === "meeting" && (
          <div>
            <label
              className={cn(
                "flex items-start gap-2 text-sm select-none",
                calStatus?.connected ? "cursor-pointer" : "cursor-not-allowed opacity-60"
              )}
            >
              <input
                type="checkbox"
                checked={addMeet && !!calStatus?.connected}
                disabled={!calStatus?.connected}
                onChange={(e) => setAddMeet(e.target.checked)}
                className="h-4 w-4 mt-0.5 accent-primary"
                data-testid="checkbox-add-meet"
              />
              <span>
                <span className="flex items-center gap-1.5 font-medium">
                  <Video className="h-3.5 w-3.5" />
                  Add Google Meet link
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  {calStatus?.connected
                    ? "Google will generate a Meet link and attach it to everyone's calendar."
                    : "Connect Google Calendar to add a Meet link."}
                </span>
              </span>
            </label>

            {/* Task #405 — transcription controls only when a Meet link is being added. */}
            {addMeet && !!calStatus?.connected && (
              <div className="mt-3 space-y-3 rounded-md border border-border bg-muted/30 p-3">
                <div>
                  <Label className="mb-1.5 block text-xs">Meeting type</Label>
                  <div className="flex gap-2">
                    {(["internal", "client"] as const).map((mt) => (
                      <button
                        key={mt}
                        type="button"
                        onClick={() => setMeetingType(mt)}
                        className={cn(
                          "flex-1 py-1 px-2 rounded-md border text-xs capitalize transition-colors",
                          meetingType === mt
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-muted-foreground border-border hover:bg-muted"
                        )}
                        data-testid={`button-meeting-type-${mt}`}
                      >
                        {mt}
                      </button>
                    ))}
                  </div>
                </div>

                {!transcriptionPolicyOn ? (
                  <p className="text-xs text-muted-foreground">
                    AI meeting notes are turned off for this workspace. An admin can enable them in Calendar settings.
                  </p>
                ) : !canTranscribeThis ? (
                  <p className="text-xs text-muted-foreground">
                    {meetingType === "client"
                      ? "Transcribing client meetings is disabled by your workspace policy."
                      : "Transcribing internal meetings is disabled by your workspace policy."}
                  </p>
                ) : !meetScopesGranted ? (
                  <div className="text-xs text-muted-foreground">
                    <p className="mb-1.5">Grant Google Meet access once to enable automatic transcripts and AI notes.</p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={connectMeetTranscription}
                      data-testid="button-connect-meet-transcription"
                    >
                      <Video className="h-3.5 w-3.5 mr-1.5" />
                      Enable Meet transcription
                    </Button>
                  </div>
                ) : (
                  <>
                    <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={transcribeRequested}
                        onChange={(e) => setTranscribeRequested(e.target.checked)}
                        className="h-4 w-4 mt-0.5 accent-primary"
                        data-testid="checkbox-transcribe-meeting"
                      />
                      <span>
                        <span className="flex items-center gap-1.5 font-medium">
                          <Sparkles className="h-3.5 w-3.5" />
                          Transcribe &amp; generate AI notes
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          Google Meet will record a transcript; we'll summarize takeaways and action items after the call.
                        </span>
                      </span>
                    </label>

                    {transcribeRequested && (
                      <label className="flex items-start gap-2 text-xs cursor-pointer select-none rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-2 text-amber-800 dark:text-amber-200">
                        <input
                          type="checkbox"
                          checked={consentAcknowledged}
                          onChange={(e) => setConsentAcknowledged(e.target.checked)}
                          className="h-4 w-4 mt-0.5 accent-amber-600"
                          data-testid="checkbox-transcribe-consent"
                        />
                        <span>
                          I'll let attendees know this {meetingType === "client" ? "client " : ""}meeting is being transcribed. Everyone in the call sees Google's recording indicator, and transcription can be stopped per-meeting from the Meetings tab.
                        </span>
                      </label>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div>
          <Label htmlFor="nevent-title">Title</Label>
          <Input
            id="nevent-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Doctor appointment"
            data-testid="input-new-event-title"
            autoFocus
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="nevent-date">Date</Label>
            <Input
              id="nevent-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="input-new-event-date"
            />
          </div>
          <div className="flex items-end pb-0.5">
            <label className="flex items-center gap-1.5 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allDay}
                onChange={(e) => setAllDay(e.target.checked)}
                className="h-4 w-4 accent-primary"
                data-testid="checkbox-all-day"
              />
              All day
            </label>
          </div>
        </div>

        {!allDay && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="nevent-start">Start time</Label>
              <Input
                id="nevent-start"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                data-testid="input-new-event-start-time"
              />
            </div>
            <div>
              <Label htmlFor="nevent-end">End time</Label>
              <Input
                id="nevent-end"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                data-testid="input-new-event-end-time"
              />
            </div>
          </div>
        )}

        <div>
          <Label htmlFor="nevent-notes">Notes (optional)</Label>
          <Textarea
            id="nevent-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            data-testid="input-new-event-notes"
          />
        </div>

        <div>
          <Label>Invitees</Label>
          <div className="flex gap-2 mt-1.5">
            <Input
              value={inviteeInput}
              onChange={(e) => setInviteeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addInvitee(); }
              }}
              placeholder="name@example.com"
              data-testid="input-new-event-invitee"
            />
            <Button
              type="button"
              variant="outline"
              onClick={addInvitee}
              disabled={!inviteeInput.trim()}
              data-testid="button-add-invitee"
            >
              Add
            </Button>
          </div>
          <Popover open={orgPickerOpen} onOpenChange={setOrgPickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="mt-1.5 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                data-testid="button-lookup-from-org"
              >
                <Users className="h-3 w-3" />
                Look up from org
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-72" align="start">
              <Command>
                <CommandInput placeholder="Search teammates..." data-testid="input-org-member-search" />
                <CommandList className="max-h-60">
                  <CommandEmpty>No teammates found.</CommandEmpty>
                  <CommandGroup>
                    {orgMembers.map((m) => {
                      const name = `${m.firstName} ${m.lastName}`.trim() || m.email;
                      const added = invitees.includes(m.email);
                      return (
                        <CommandItem
                          key={m.id}
                          value={`${name} ${m.email} ${m.role}`}
                          disabled={added || !m.email}
                          onSelect={() => {
                            if (!added && m.email) {
                              setInvitees((prev) => [...prev, m.email]);
                            }
                          }}
                          className={cn("flex items-center justify-between gap-2", added && "opacity-50")}
                          data-testid={`item-org-member-${m.id}`}
                        >
                          <div className="min-w-0">
                            <div className="truncate text-sm">{name}</div>
                            <div className="truncate text-xs text-muted-foreground">
                              {m.email}{m.role ? ` · ${m.role}` : ""}
                            </div>
                          </div>
                          {added && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {invitees.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {invitees.map(email => (
                <Badge key={email} variant="secondary" className="gap-1 pr-1 text-xs">
                  {email}
                  <button
                    type="button"
                    onClick={() => setInvitees(prev => prev.filter(e => e !== email))}
                    className="ml-0.5 hover:text-destructive"
                    data-testid={`button-remove-invitee-${email}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => submit.mutate()}
          disabled={invalid || submit.isPending}
          data-testid="button-create-calendar-event"
        >
          {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Add to calendar
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function DayDetailView({
  year,
  month,
  day,
  gcalEvents,
  scheduleEntries,
  onBack,
  onAddEvent,
}: {
  year: number;
  month: number;
  day: number;
  gcalEvents: GCalEvent[];
  scheduleEntries: ScheduleEntry[];
  onBack: () => void;
  onAddEvent: () => void;
}) {
  const dateLabel = new Date(year, month, day).toLocaleDateString([], {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  const sorted = [...gcalEvents].sort((a, b) => {
    const aTime = a.start.dateTime ? new Date(a.start.dateTime).getTime() : 0;
    const bTime = b.start.dateTime ? new Date(b.start.dateTime).getTime() : 0;
    return aTime - bTime;
  });

  return (
    <Card data-testid="panel-day-detail">
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3 gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="button-day-detail-back">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <CardTitle className="text-base">{dateLabel}</CardTitle>
        </div>
        <Button
          size="sm"
          className="gap-1.5 bg-green-600 hover:bg-green-700 text-white"
          onClick={onAddEvent}
          data-testid="button-day-detail-add-event"
        >
          <Plus className="h-3.5 w-3.5" />
          Add event
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {scheduleEntries.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Booking</h4>
            <div className="space-y-1">
              {scheduleEntries.map(entry => (
                <div key={entry.id} className={cn("text-sm px-3 py-2 rounded-md border", getStatusColor(entry.status))}>
                  <span className="font-medium">{entry.status}</span>
                  {entry.city && <span className="ml-2 text-muted-foreground">{entry.city}{entry.state ? `, ${entry.state}` : ''}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Calendar events</h4>
          {sorted.length === 0 ? (
            <p className="text-sm text-muted-foreground italic" data-testid="text-no-events-day">
              No events on this day. Hit "Add event" to create one.
            </p>
          ) : (
            <div className="space-y-2">
              {sorted.map(evt => {
                const startTime = evt.start.dateTime
                  ? new Date(evt.start.dateTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                  : 'All day';
                const endTime = evt.end.dateTime
                  ? new Date(evt.end.dateTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                  : '';
                return (
                  <div key={evt.id} className="flex items-start gap-3 rounded-md border p-3" data-testid={`row-day-event-${evt.id}`}>
                    <div className="w-16 text-xs text-muted-foreground tabular-nums shrink-0">
                      <div>{startTime}</div>
                      {endTime && endTime !== startTime && (
                        <>
                          <div className="opacity-50">to</div>
                          <div>{endTime}</div>
                        </>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{evt.summary || '(No title)'}</div>
                      {evt.location && (
                        <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span className="truncate">{evt.location}</span>
                        </div>
                      )}
                      {evt.description && (
                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{evt.description}</div>
                      )}
                      {evt.attendees && evt.attendees.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Users className="h-3 w-3 shrink-0" />
                          <span className="truncate">
                            {evt.attendees.slice(0, 3).map(a => a.displayName || a.email).join(', ')}
                            {evt.attendees.length > 3 ? ` +${evt.attendees.length - 3}` : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function GoogleCalendarView({ year, month, newEventOpen, setNewEventOpen, newEventDefaultDate, setNewEventDefaultDate, viewingUserId, viewingUserName, viewingWorkspace }: {
  year: number;
  month: number;
  newEventOpen: boolean;
  setNewEventOpen: (v: boolean) => void;
  newEventDefaultDate: string;
  setNewEventDefaultDate: (v: string) => void;
  viewingUserId: string | null;
  viewingUserName: string;
  viewingWorkspace: boolean;
}) {
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  // Task #410 — "My Calendar" is the only mode with editing/day-detail. Both
  // teammate and workspace views are read-only.
  const isOwnCalendar = !viewingUserId && !viewingWorkspace;

  const { data: calStatus } = useQuery<{ connected: boolean; email: string | null }>({
    queryKey: ["/api/user/calendar/status"],
  });

  const from = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const to = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  const { data: userGcalEvents = [], isLoading: gcalLoading } = useQuery<GCalEvent[]>({
    queryKey: ["/api/user/calendar/events", { from, to }],
    queryFn: async () => {
      const params = new URLSearchParams({ from, to });
      const res = await fetch(`/api/user/calendar/events?${params}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!calStatus?.connected && isOwnCalendar,
  });

  // Task #410 — workspace-level (shared/fallback) calendar events. Mirrors the
  // GCalEvent[] shape so it feeds the same rendering path as "My Calendar".
  const { data: workspaceGcalEvents = [], isLoading: workspaceLoading } = useQuery<GCalEvent[]>({
    queryKey: ["/api/calendar/workspace/events", { from, to }],
    queryFn: async () => {
      const params = new URLSearchParams({ from, to });
      const res = await fetch(`/api/calendar/workspace/events?${params}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: viewingWorkspace,
  });

  const gcalEvents = viewingWorkspace ? workspaceGcalEvents : userGcalEvents;

  const { data: gridData } = useQuery<GridData>({
    queryKey: ["/api/calendar-hub/grid", { year, month }],
    queryFn: async () => {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      const response = await fetch(`/api/calendar-hub/grid?${params}`);
      if (!response.ok) throw new Error("Failed to fetch grid data");
      return response.json();
    },
    enabled: isOwnCalendar,
  });

  const { data: teammateGridData, isLoading: teammateLoading } = useQuery<TeammateGridData>({
    queryKey: ["/api/calendar/teammate", viewingUserId, "grid", { year, month }],
    queryFn: async () => {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      const response = await fetch(`/api/calendar/teammate/${viewingUserId}/grid?${params}`);
      if (!response.ok) throw new Error("Failed to fetch teammate calendar");
      return response.json();
    },
    enabled: !!viewingUserId,
  });

  const firstDayOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();

  const days: (number | null)[] = [];
  for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const gcalByDate: Record<string, GCalEvent[]> = {};
  for (const evt of gcalEvents) {
    const dateStr = evt.start.dateTime ? evt.start.dateTime.split('T')[0] : evt.start.date || '';
    if (!gcalByDate[dateStr]) gcalByDate[dateStr] = [];
    gcalByDate[dateStr].push(evt);
  }

  const entries = viewingWorkspace
    ? {}
    : (viewingUserId ? teammateGridData?.entries : gridData?.entries) || {};
  const isAvailabilityOnly = viewingUserId && teammateGridData?.sharingMode === "availability_only";
  const todayStr = new Date().toISOString().split('T')[0];

  const openNewEvent = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setNewEventDefaultDate(dateStr);
    setNewEventOpen(true);
  };

  if (selectedDay !== null && isOwnCalendar) {
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    return (
      <>
        <DayDetailView
          year={year}
          month={month}
          day={selectedDay}
          gcalEvents={gcalByDate[dateKey] || []}
          scheduleEntries={entries[dateKey] || []}
          onBack={() => setSelectedDay(null)}
          onAddEvent={() => openNewEvent(selectedDay)}
        />
        <Dialog open={newEventOpen} onOpenChange={setNewEventOpen}>
          <NewEventDialog defaultDate={newEventDefaultDate} onClose={() => setNewEventOpen(false)} />
        </Dialog>
      </>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        {viewingWorkspace ? (
          <div className="mb-3 flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm" data-testid="banner-viewing-workspace">
            <Calendar className="h-4 w-4 text-primary shrink-0" />
            <span className="text-foreground font-medium">Viewing the workspace calendar</span>
            <span className="ml-auto text-xs text-muted-foreground italic">View only</span>
          </div>
        ) : viewingUserId ? (
          <div className="mb-3 flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm" data-testid="banner-viewing-teammate">
            <Users className="h-4 w-4 text-primary shrink-0" />
            <span className="text-foreground font-medium">Viewing {viewingUserName}'s calendar</span>
            {isAvailabilityOnly && (
              <span className="ml-auto text-xs text-muted-foreground italic">Availability only — details hidden</span>
            )}
          </div>
        ) : !calStatus?.connected && (
          <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 px-3 py-2 text-sm">
            <span className="text-amber-800 dark:text-amber-200">Connect Google Calendar to see your personal events here.</span>
            <Link href="/settings/integrations">
              <Button size="sm" variant="outline" className="gap-1.5 shrink-0" data-testid="button-connect-calendar">
                <ExternalLink className="h-3 w-3" /> Connect
              </Button>
            </Link>
          </div>
        )}

        {(gcalLoading || teammateLoading || workspaceLoading) && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
            <div key={day} className="text-center text-xs font-semibold text-muted-foreground py-2 uppercase tracking-wide">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="min-h-[96px] rounded bg-muted/10" />;
            }

            const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayEntries = entries[dateKey] || [];
            const dayGcal = viewingUserId ? [] : (gcalByDate[dateKey] || []);
            const dayBlocks: TeammateBlock[] = viewingUserId ? (teammateGridData?.blocks?.[dateKey] || []) : [];
            const isToday = todayStr === dateKey;
            const totalCount = dayEntries.length + dayGcal.length + dayBlocks.length;

            return (
              <div
                key={day}
                className={cn(
                  "min-h-[96px] border rounded-md p-1 transition-colors select-none",
                  isOwnCalendar && "cursor-pointer hover:bg-accent/40",
                  isToday ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border"
                )}
                onDoubleClick={() => isOwnCalendar && setSelectedDay(day)}
                data-testid={`cell-calendar-day-${dateKey}`}
              >
                <div className={cn(
                  "text-sm font-semibold mb-1 px-0.5 flex items-center justify-between",
                  isToday ? "text-primary" : "text-foreground"
                )}>
                  <span className={cn(
                    "h-6 w-6 flex items-center justify-center rounded-full text-sm",
                    isToday && "bg-primary text-primary-foreground"
                  )}>
                    {day}
                  </span>
                </div>

                <div className="space-y-0.5">
                  {dayEntries.slice(0, 1).map(entry => (
                    <div
                      key={entry.id}
                      className={cn(
                        "text-[10px] px-1 py-0.5 rounded truncate border font-medium",
                        isAvailabilityOnly
                          ? "bg-muted/40 text-muted-foreground border-muted/60"
                          : getStatusColor(entry.status)
                      )}
                      title={isAvailabilityOnly ? "Busy" : `${entry.status}: ${entry.city || ''} ${entry.state || ''}`}
                    >
                      {isAvailabilityOnly ? "Busy" : (entry.status || "Booked")}
                    </div>
                  ))}
                  {dayBlocks.slice(0, dayEntries.length >= 1 ? 1 : 2).map(block => {
                    const timeStr = new Date(block.startAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                    return (
                      <div
                        key={block.id}
                        className={cn(
                          "text-[10px] px-1 py-0.5 rounded truncate border font-medium",
                          getBlockChipClass(block)
                        )}
                        title={block.isBusy ? "Busy" : `${block.title} (${timeStr})`}
                      >
                        {block.isBusy ? "Busy" : (
                          <><span className="opacity-70 mr-0.5">{timeStr}</span>{block.title}</>
                        )}
                      </div>
                    );
                  })}
                  {dayGcal.slice(0, 2).map(evt => {
                    const timeStr = evt.start.dateTime
                      ? new Date(evt.start.dateTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                      : '';
                    return (
                      <div
                        key={evt.id}
                        className="text-[10px] px-1 py-0.5 rounded truncate bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800"
                        title={evt.summary}
                      >
                        {timeStr && <span className="opacity-70 mr-0.5">{timeStr}</span>}
                        {evt.summary}
                      </div>
                    );
                  })}
                  {totalCount > 3 && (
                    <div className="text-[10px] text-muted-foreground px-1">
                      +{totalCount - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4">
          {viewingWorkspace
            ? <p className="text-xs text-muted-foreground">Viewing the shared workspace calendar (view only). Switch back to "My Calendar" to edit.</p>
            : viewingUserId
            ? <p className="text-xs text-muted-foreground">Viewing {viewingUserName}'s CRM schedule. Switch back to "My Calendar" to edit.</p>
            : <p className="text-xs text-muted-foreground">Double-click a day to see the full breakdown.</p>
          }
        </div>
      </CardContent>

      <Dialog open={newEventOpen} onOpenChange={setNewEventOpen}>
        <NewEventDialog defaultDate={newEventDefaultDate} onClose={() => setNewEventOpen(false)} />
      </Dialog>
    </Card>
  );
}

// (TimelineView removed — CalendarHub now uses GoogleCalendarView)


export default function CalendarsPage() {
  const [activeTab, setActiveTab] = useState("calendar");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [newEventDefaultDate, setNewEventDefaultDate] = useState("");
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);
  // Task #410 — when true, the Hub shows the workspace-level (shared/fallback)
  // Google Calendar instead of "My Calendar" or a teammate.
  const [viewingWorkspace, setViewingWorkspace] = useState(false);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  const initialPanelView = typeof window !== "undefined"
    ? (new URLSearchParams(window.location.search).get("panel") as "coach" | "lodging" | "meetings" | "conflicts" | null)
    : null;

  const { data: calendarMembers = [] } = useQuery<Array<{ id: string; firstName: string; lastName: string; role: string; calendarSharingMode: string }>>({
    queryKey: ["/api/calendar/members"],
    queryFn: async () => {
      const res = await fetch("/api/calendar/members");
      if (!res.ok) return [];
      return res.json();
    },
  });

  const visibleMembers = calendarMembers.filter(
    m => m.calendarSharingMode === "shared" || m.calendarSharingMode === "availability_only"
  );

  // Task #410 — the workspace-level calendar is selectable only when the admin
  // toggle is on AND a workspace-level calendar is actually connected.
  const { data: calHubSettings } = useQuery<{ showWorkspaceCalendarInHub?: boolean; workspaceCalendarConnected?: boolean }>({
    queryKey: ["/api/calendar/settings"],
  });
  const showWorkspaceCalendarOption =
    !!calHubSettings?.showWorkspaceCalendarInHub && !!calHubSettings?.workspaceCalendarConnected;

  const viewingMember = visibleMembers.find(m => m.id === viewingUserId);
  const viewingUserName = viewingMember
    ? [viewingMember.firstName, viewingMember.lastName].filter(Boolean).join(" ") || "Teammate"
    : "";

  return (
    <div className="p-3 sm:p-6 flex flex-col lg:flex-row gap-4 sm:gap-6 min-h-0">
      <div className="flex-1 min-w-0 space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Calendar className="h-6 sm:h-8 w-6 sm:w-8 text-primary" />
            <h1 className="text-xl sm:text-2xl font-bold" data-testid="text-calendar-hub-title">Calendar Hub</h1>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (selectedMonth === 0) {
                  setSelectedMonth(11);
                  setSelectedYear(selectedYear - 1);
                } else {
                  setSelectedMonth(selectedMonth - 1);
                }
              }}
              data-testid="button-prev-month"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[130px]" data-testid="select-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((month, idx) => (
                  <SelectItem key={month} value={String(idx)}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[90px]" data-testid="select-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (selectedMonth === 11) {
                  setSelectedMonth(0);
                  setSelectedYear(selectedYear + 1);
                } else {
                  setSelectedMonth(selectedMonth + 1);
                }
              }}
              data-testid="button-next-month"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center gap-2 flex-wrap">
            <TabsList>
              <TabsTrigger value="calendar" className="gap-2" data-testid="tab-calendar">
                <CalendarDays className="h-4 w-4" />
                Calendar
              </TabsTrigger>
              <TabsTrigger value="meetings" className="gap-2" data-testid="tab-meetings">
                <FileText className="h-4 w-4" />
                Meetings
              </TabsTrigger>
              <TabsTrigger value="aspirations" className="gap-2" data-testid="tab-aspirations">
                <Target className="h-4 w-4" />
                Aspirations
              </TabsTrigger>
            </TabsList>
            <Button
              size="icon"
              className="h-9 w-9 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm"
              onClick={() => {
                const from = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`;
                setNewEventDefaultDate(from);
                setNewEventOpen(true);
              }}
              data-testid="button-add-calendar-event"
              aria-label="Add calendar event"
            >
              <Plus className="h-5 w-5" />
            </Button>

            <div className="ml-auto">
              <Select
                value={viewingWorkspace ? "__workspace__" : (viewingUserId ?? "__mine__")}
                onValueChange={(v) => {
                  if (v === "__workspace__") {
                    setViewingWorkspace(true);
                    setViewingUserId(null);
                  } else {
                    setViewingWorkspace(false);
                    setViewingUserId(v === "__mine__" ? null : v);
                  }
                }}
                data-testid="select-viewing-user"
              >
                <SelectTrigger className="w-[180px] gap-2" data-testid="trigger-viewing-user">
                  <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__mine__">My Calendar</SelectItem>
                  {showWorkspaceCalendarOption && (
                    <SelectItem value="__workspace__" data-testid="option-viewing-workspace">
                      Workspace Calendar
                    </SelectItem>
                  )}
                  {visibleMembers.map(m => (
                    <SelectItem key={m.id} value={m.id} data-testid={`option-viewing-user-${m.id}`}>
                      {[m.firstName, m.lastName].filter(Boolean).join(" ") || m.id}
                      {m.calendarSharingMode === "availability_only" && (
                        <span className="ml-1 text-xs text-muted-foreground">(avail.)</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4">
            <TabsContent value="calendar" className="m-0">
              <GoogleCalendarView
                year={selectedYear}
                month={selectedMonth}
                newEventOpen={newEventOpen}
                setNewEventOpen={setNewEventOpen}
                newEventDefaultDate={newEventDefaultDate}
                setNewEventDefaultDate={setNewEventDefaultDate}
                viewingUserId={viewingUserId}
                viewingUserName={viewingUserName}
                viewingWorkspace={viewingWorkspace}
              />
            </TabsContent>

            <TabsContent value="meetings" className="m-0">
              <MeetingsView />
            </TabsContent>

            <TabsContent value="aspirations" className="m-0">
              <AspirationsView />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      <ConflictsPanel defaultView={initialPanelView} />
    </div>
  );
}

// ============================================================================
// Task #3 — Calendar Hub conflicts side panel
// ============================================================================
// Collapsible right-hand panel listing the workspace's open conflicts
// detected by the Conflict Radar engine. The radar's dedicated page
// has been retired; this panel + per-rep notifications are now the
// only user-facing surface. Each row has:
//   • Ignore  — acknowledges the conflict (it leaves the panel).
//   • Resolve — jumps to the related entity (the conflicting invoice,
//     the rep's dashboard, etc.) so the user can actually fix it.
// When the workspace has zero open conflicts the panel auto-collapses
// to a slim "no conflicts" indicator that won't crowd the calendar.

const CONFLICT_KIND_LABEL: Record<string, string> = {
  talent_double_booking: "Double booking",
  venue_vs_tour: "Venue vs tour",
  rep_overcommitted: "Rep overload",
};

const CONFLICT_SEVERITY_STYLE: Record<string, string> = {
  high: "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200 border-red-300 dark:border-red-700",
  medium: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 border-amber-300 dark:border-amber-700",
  low: "bg-muted text-muted-foreground border-border",
};

function conflictResolveHref(c: Conflict): string {
  const subjectIds = (c.subjectIds as unknown as string[]) ?? [];
  if (c.kind === "talent_double_booking" && subjectIds[1]) {
    return `/invoices?conflict=${c.id}&focus=${subjectIds[1]}`;
  }
  if (c.kind === "venue_vs_tour" && subjectIds[0]) {
    return `/invoices?conflict=${c.id}&focus=${subjectIds[0]}`;
  }
  if (c.kind === "rep_overcommitted") {
    return `/dashboard?conflict=${c.id}`;
  }
  return `/calendars?panel=conflicts&conflict=${c.id}`;
}

function ConflictsPanel({ defaultView }: { defaultView: "coach" | "lodging" | "meetings" | "conflicts" | null }) {
  const { toast } = useToast();
  const { data: conflicts = [], isLoading } = useQuery<Conflict[]>({
    queryKey: ["/api/conflicts", { status: "open" }],
    queryFn: async () => {
      const res = await fetch("/api/conflicts?status=open", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // Pending lodging count for the badge (deduped with LodgingView's queryKey)
  const { data: pendingSuggestions = [] } = useQuery<LodgingSuggestion[]>({
    queryKey: ["/api/lodging-suggestions", { status: "pending" }],
    queryFn: async () => {
      const res = await fetch("/api/lodging-suggestions?status=pending");
      if (!res.ok) return [];
      return res.json();
    },
  });
  const pendingLodgingCount = pendingSuggestions.length;

  const openCount = conflicts.length;
  const shouldAutoOpen = !!defaultView || openCount > 0;
  const [manualCollapsed, setManualCollapsed] = useState<boolean | null>(null);
  const collapsed = manualCollapsed === null ? !shouldAutoOpen : manualCollapsed;
  const [view, setView] = useState<"coach" | "lodging" | "meetings" | "conflicts">(
    defaultView ?? "coach"
  );

  const { setCalendarCoachTarget, setCalendarPanelMounted, setOpenCalendarCoachPanel } = useVoiceAssistantState();
  const prefersReducedMotion = useReducedMotion();
  const coachTargetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (collapsed) {
      setCalendarPanelMounted(false);
      setCalendarCoachTarget(null);
      return;
    }
    setCalendarPanelMounted(true);
    return () => {
      setCalendarPanelMounted(false);
      setCalendarCoachTarget(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed]);

  useEffect(() => {
    if (!collapsed && view === "coach") {
      setCalendarCoachTarget(coachTargetRef.current);
    } else {
      setCalendarCoachTarget(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, collapsed]);

  // Register a callback in global state so the AI orb can programmatically
  // expand this panel to the Coach tab (e.g. when the user taps the FAB on
  // the Calendar Hub page). Cleaned up on unmount.
  useEffect(() => {
    setOpenCalendarCoachPanel(() => {
      setManualCollapsed(false);
      setView("coach");
    });
    return () => setOpenCalendarCoachPanel(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ackMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("PATCH", `/api/conflicts/${id}/acknowledge`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/conflicts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
      toast({ title: "Conflict ignored", description: "It won't appear again unless it re-opens." });
    },
    onError: (err: any) => {
      toast({ title: "Couldn't ignore the conflict", description: err?.message, variant: "destructive" });
    },
  });

  if (collapsed) {
    return (
      <div className="lg:w-12 lg:shrink-0">
        <Button
          variant="outline"
          className="w-full lg:w-12 lg:h-auto lg:py-3 lg:flex-col lg:gap-2 justify-center"
          onClick={() => setManualCollapsed(false)}
          data-testid="button-open-conflicts-panel"
          aria-label="Open calendar side panel"
        >
          <ChevronsLeft className="h-4 w-4 hidden lg:block" />
          <Sparkles className="h-4 w-4 text-primary" />
          {openCount > 0 && <AlertTriangle className="h-4 w-4 text-amber-500" />}
          {pendingLodgingCount > 0 && <BedDouble className="h-4 w-4 text-blue-400" />}
          <span className="lg:[writing-mode:vertical-rl] lg:rotate-180 text-xs">
            Coach · Lodging · Meetings · Conflicts{openCount > 0 ? ` (${openCount})` : ""}
          </span>
        </Button>
      </div>
    );
  }

  const morphLayoutId = prefersReducedMotion ? undefined : "ai-assistant-tour-morph";
  const morphTransition = prefersReducedMotion
    ? { duration: 0.18 }
    : { type: "spring" as const, stiffness: 320, damping: 32, mass: 0.9 };

  const tabBtn = (
    v: typeof view,
    icon: React.ReactNode,
    label: string,
    badge?: number
  ) => (
    <button
      type="button"
      onClick={() => setView(v)}
      data-testid={`button-side-panel-view-${v}`}
      className={cn(
        "flex items-center gap-1 px-2 py-1 rounded-md transition-colors text-[11px] font-medium",
        view === v
          ? "bg-background shadow-sm text-foreground ring-1 ring-border"
          : "text-muted-foreground hover:text-foreground"
      )}
    >
      {icon}
      <span>{label}</span>
      {badge != null && badge > 0 && (
        <Badge
          variant={view === v ? "secondary" : "destructive"}
          className="ml-0.5 h-4 px-1 text-[10px]"
        >
          {badge > 99 ? "99+" : badge}
        </Badge>
      )}
    </button>
  );

  return (
    <motion.div
      layoutId={morphLayoutId}
      transition={morphTransition}
      className="lg:w-80 lg:shrink-0"
      data-testid="panel-calendar-hub-side"
    >
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3 gap-2">
          <div className="flex items-center gap-0.5 rounded-lg bg-muted p-1 flex-wrap">
            {tabBtn("coach", <Sparkles className="h-3 w-3" />, "Coach")}
            {tabBtn("lodging", <BedDouble className="h-3 w-3" />, "Lodging", pendingLodgingCount)}
            {tabBtn("meetings", <UserPlus className="h-3 w-3" />, "Meetings")}
            {tabBtn(
              "conflicts",
              <AlertTriangle className={cn("h-3 w-3", openCount > 0 && view !== "conflicts" && "text-amber-500")} />,
              "Conflicts",
              openCount
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setManualCollapsed(true)}
            aria-label="Collapse side panel"
            data-testid="button-collapse-conflicts-panel"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {view === "coach" && (
            <div className="w-full">
              {/* Coach v2 (Task #311) — quantified aspiration progress bars sit
                  above the voice-assistant portal target so the user sees their
                  long-term goals fill up alongside the coaching conversation. */}
              <CoachAspirationsPanel />
              <div
                ref={coachTargetRef}
                data-testid="container-calendar-coach"
                className="relative min-h-[520px] w-full"
              />
            </div>
          )}

          {view === "lodging" && (
            <div className="p-3 pt-1" data-testid="container-side-lodging">
              <LodgingView />
            </div>
          )}

          {view === "meetings" && (
            <div className="p-3 pt-1" data-testid="container-side-meetings">
              <MeetingRequestsView />
            </div>
          )}

          {view === "conflicts" && (
            <div className="space-y-2 p-4 pt-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : openCount === 0 ? (
                <div className="text-sm text-muted-foreground italic py-3" data-testid="text-conflicts-empty">
                  No open conflicts. The radar is still watching in the background.
                </div>
              ) : (
                (["talent_double_booking", "venue_vs_tour", "rep_overcommitted"] as const).map((kind) => {
                  const group = conflicts.filter((c) => c.kind === kind);
                  if (group.length === 0) return null;
                  return (
                    <div key={kind} className="space-y-2" data-testid={`group-conflict-${kind}`}>
                      <div className="flex items-center gap-2 pt-1">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {CONFLICT_KIND_LABEL[kind]}
                        </h4>
                        <Badge variant="secondary" className="text-xs">{group.length}</Badge>
                      </div>
                      {group.map((c) => {
                        const sevStyle = CONFLICT_SEVERITY_STYLE[c.severity] ?? CONFLICT_SEVERITY_STYLE.low;
                        const href = conflictResolveHref(c);
                        return (
                          <div
                            key={c.id}
                            className="rounded-md border p-2 space-y-2"
                            data-testid={`row-conflict-${c.id}`}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className={cn("text-xs", sevStyle)} data-testid={`badge-conflict-severity-${c.id}`}>
                                {c.severity}
                              </Badge>
                              {c.status === "acknowledged" && (
                                <Badge variant="secondary" className="text-xs">Acknowledged</Badge>
                              )}
                            </div>
                            <div className="text-sm leading-snug" data-testid={`text-conflict-summary-${c.id}`}>
                              {c.summary}
                            </div>
                            <div className="flex items-center gap-2">
                              <Link href={href}>
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="h-7 gap-1"
                                  data-testid={`button-conflict-resolve-${c.id}`}
                                >
                                  Resolve
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                              </Link>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7"
                                disabled={ackMutation.isPending && ackMutation.variables === c.id}
                                onClick={() => ackMutation.mutate(c.id)}
                                data-testid={`button-conflict-ignore-${c.id}`}
                              >
                                {ackMutation.isPending && ackMutation.variables === c.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  "Ignore"
                                )}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ============================================================================
// Calendar Hub Slice 1 — Time Blocks panel
// ============================================================================
// Shows the current user's three-layer week (Fixed / Fluid / Life) with a
// single "Add block" CTA. The slice-1 UI is intentionally simple — a list
// grouped by day with a layer badge + a visibility badge per block — so we
// can ship the data model and start using it right away. Slice 2 wires up
// show-driven cascade; slice 3 layers the chatbot on top.

const LAYER_STYLES: Record<CalendarBlockLayer, { label: string; className: string }> = {
  fixed: { label: "Fixed", className: "bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200 border-blue-300 dark:border-blue-700" },
  fluid: { label: "Fluid", className: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 border-amber-300 dark:border-amber-700" },
  life:  { label: "Life",  className: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700" },
};

const VISIBILITY_LABEL: Record<CalendarBlockVisibility, { label: string; icon: typeof Lock; description: string }> = {
  private: { label: "Private",  icon: Lock,   description: "Only you can see the details. Others see 'Busy'." },
  role:    { label: "My role",  icon: Users,  description: "Visible to teammates with the same role." },
  org:     { label: "Everyone", icon: Globe,  description: "Visible to your whole workspace." },
};

const KIND_OPTIONS: Array<{ value: CalendarBlockKind; label: string }> = [
  { value: "user_fixed",    label: "Personal commitment" },
  { value: "focus",         label: "Focus block" },
  { value: "errand",        label: "Errand" },
  { value: "meal",          label: "Meal" },
  { value: "sleep",         label: "Sleep" },
  { value: "decompression", label: "Decompression" },
  { value: "buffer",        label: "Buffer" },
  { value: "other",         label: "Other" },
];

function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  // Week starts Monday — change to 0 for Sunday if you prefer.
  const dow = (out.getDay() + 6) % 7;
  out.setDate(out.getDate() - dow);
  return out;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function fmtDayHeader(d: Date): string {
  return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
}

function toLocalInputValue(d: Date): string {
  // datetime-local expects "YYYY-MM-DDTHH:mm" in local time.
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type CalendarMember = { userId: string; name: string; email: string; role: string };
type CalendarMeetingRequest = {
  id: string;
  requesterId: string;
  requesterName?: string;
  recipientId: string;
  recipientName?: string;
  proposedStart: string;
  proposedEnd: string;
  title: string;
  message: string | null;
  status: "pending" | "accepted" | "declined";
  createdAt: string;
};

// ── Aspirations tab ────────────────────────────────────────────────────
// Replaces the old "Time Blocks" tab. Aspirations are the long-term goals
// that drive the auto-scheduler: each quantified aspiration shows a stacked
// progress bar — green = confirmed progress, yellow = assumed (inferred from
// completed coach blocks, not yet confirmed). Editing an aspiration re-runs
// the runway so the calendar refills toward the deadline. Manual one-off
// blocks are still creatable here via the same block editor.

type AspirationCard = {
  id: string;
  title: string;
  why: string | null;
  domain: string;
  status: string;
  unit: string | null;
  target: number | null;
  current: number;
  pct: number | null;
  assumed: number;
  confirmedHours?: number;
  assumedHours?: number;
  futurePlannedHours?: number;
  targetDate: string | null;
  ratePerHour: string | null;
  rateUnitLabel: string | null;
  recommendedHoursPerWeek: string | null;
  committedHoursPerWeek: string | null;
  preferredDays: number[] | null;
  preferredHourOfDay: number | null;
  autoScheduleEnabled: boolean;
  lastScheduledThrough: string | null;
  completedAt: string | null;
  // One-time AI roadmap written when the aspiration was first scheduled.
  planOfAction: string | null;
  planGeneratedAt: string | null;
  planBlockSizeMinutes: number | null;
};

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// ── Task #405 — Meetings (Google Meet transcripts + AI notes) ────────────────
export type MeetingRecord = {
  id: string;
  title: string | null;
  meetingType: string;
  clientId: string | null;
  status: string;
  notesStatus: string;
  startAt: string | null;
  endAt: string | null;
  meetingUri: string | null;
  transcribeRequested: boolean;
  transcriptText: string | null;
  summary: string | null;
  keyTakeaways: string[] | null;
  actionItems: string[] | null;
  importedAt: string | null;
  lastError: string | null;
};

const MEETING_STATUS_LABELS: Record<string, { label: string; className: string }> = {
  disabled: { label: "No transcription", className: "bg-muted text-muted-foreground" },
  scheduled: { label: "Awaiting call", className: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300" },
  polling: { label: "Processing", className: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" },
  imported: { label: "Transcript ready", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" },
  completed: { label: "Notes ready", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" },
  no_transcript: { label: "No transcript", className: "bg-muted text-muted-foreground" },
  unavailable: { label: "Unavailable", className: "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300" },
};

export function MeetingNotesContent({ meeting }: { meeting: MeetingRecord }) {
  if (meeting.notesStatus === "generating") {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-notes-generating">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating AI notes…
      </p>
    );
  }
  if (meeting.notesStatus !== "ready" && !meeting.summary) {
    if (meeting.lastError) {
      return <p className="text-sm text-muted-foreground">{meeting.lastError}</p>;
    }
    if (meeting.status === "scheduled" || meeting.status === "polling") {
      return <p className="text-sm text-muted-foreground">Notes will appear here after the meeting ends.</p>;
    }
    return <p className="text-sm text-muted-foreground">No AI notes for this meeting.</p>;
  }
  return (
    <div className="space-y-3">
      {meeting.summary && (
        <div>
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Summary</h5>
          <p className="text-sm whitespace-pre-wrap" data-testid="text-meeting-summary">{meeting.summary}</p>
        </div>
      )}
      {!!meeting.keyTakeaways?.length && (
        <div>
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Key takeaways</h5>
          <ul className="space-y-1">
            {meeting.keyTakeaways.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" data-testid={`text-takeaway-${i}`}>
                <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-600 shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!!meeting.actionItems?.length && (
        <div>
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Action items</h5>
          <ul className="space-y-1">
            {meeting.actionItems.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" data-testid={`text-action-${i}`}>
                <ListChecks className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function MeetingCard({ meeting, onStop, stopping }: { meeting: MeetingRecord; onStop?: (id: string) => void; stopping?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const statusMeta = MEETING_STATUS_LABELS[meeting.status] ?? { label: meeting.status, className: "bg-muted text-muted-foreground" };
  const when = meeting.startAt ? new Date(meeting.startAt).toLocaleString() : "—";
  const canStop = meeting.transcribeRequested && (meeting.status === "scheduled" || meeting.status === "polling");
  return (
    <Card data-testid={`card-meeting-${meeting.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium truncate" data-testid={`text-meeting-title-${meeting.id}`}>
                {meeting.title || "Untitled meeting"}
              </span>
              <Badge variant="outline" className="capitalize text-xs">{meeting.meetingType}</Badge>
              <Badge className={cn("text-xs border-0", statusMeta.className)} data-testid={`badge-meeting-status-${meeting.id}`}>
                {statusMeta.label}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{when}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {meeting.meetingUri && (
              <a href={meeting.meetingUri} target="_blank" rel="noreferrer" data-testid={`link-meeting-${meeting.id}`}>
                <Button size="sm" variant="ghost" className="h-8"><Video className="h-3.5 w-3.5" /></Button>
              </a>
            )}
            {canStop && onStop && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-rose-600"
                onClick={() => onStop(meeting.id)}
                disabled={stopping}
                data-testid={`button-stop-transcription-${meeting.id}`}
              >
                <StopCircle className="h-3.5 w-3.5 mr-1" /> Stop
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-8"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`button-toggle-meeting-${meeting.id}`}
            >
              {expanded ? "Hide" : "View notes"}
            </Button>
          </div>
        </div>
        {expanded && (
          <div className="mt-3 border-t pt-3">
            <MeetingNotesContent meeting={meeting} />
            {meeting.transcriptText && (
              <details className="mt-3">
                <summary className="text-xs font-medium text-muted-foreground cursor-pointer" data-testid={`summary-transcript-${meeting.id}`}>
                  Full transcript
                </summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-2 text-xs">
                  {meeting.transcriptText}
                </pre>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MeetingsView() {
  const { toast } = useToast();
  const { data: meetings = [], isLoading } = useQuery<MeetingRecord[]>({
    queryKey: ["/api/meetings"],
    refetchInterval: 30_000,
  });

  const stopMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/meetings/${id}/stop-transcription`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meetings"] });
      toast({ title: "Transcription stopped for this meeting" });
    },
    onError: (err: any) => {
      toast({ title: "Couldn't stop transcription", description: err?.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (meetings.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-10 text-center" data-testid="empty-meetings">
        <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
        <p className="font-medium">No meetings yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Add a calendar event of type "meeting" with a Google Meet link to capture transcripts and AI notes here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="list-meetings">
      {meetings.map((m) => (
        <MeetingCard key={m.id} meeting={m} onStop={(id) => stopMutation.mutate(id)} stopping={stopMutation.isPending} />
      ))}
    </div>
  );
}

function AspirationsView() {
  const { toast } = useToast();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AspirationCard | null>(null);
  const [blockOpen, setBlockOpen] = useState(false);

  const { data: aspirations = [], isLoading } = useQuery<AspirationCard[]>({
    queryKey: ["/api/coach/aspirations", { status: "active" }],
    refetchInterval: 30_000,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/coach/aspirations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/coach/aspirations"] });
      toast({ title: "Aspiration removed" });
    },
    onError: () => toast({ title: "Couldn't remove the aspiration", variant: "destructive" }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold">Aspirations</h2>
          <p className="text-sm text-muted-foreground">
            Long-term goals your coach schedules toward. Green is confirmed progress, yellow is assumed until you confirm it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="button-add-block">
                <Plus className="h-4 w-4" />
                Time block
              </Button>
            </DialogTrigger>
            <BlockEditorDialog mode="create" defaultStart={new Date()} onClose={() => setBlockOpen(false)} />
          </Dialog>
          <Button
            className="gap-2"
            data-testid="button-add-aspiration"
            onClick={() => { setEditing(null); setEditorOpen(true); }}
          >
            <Target className="h-4 w-4" />
            New aspiration
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : aspirations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="text-aspirations-empty">
            No aspirations yet. Add one — or just tell your coach what you're working toward and it'll set the plan.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {aspirations.map((a) => (
            <AspirationCardView
              key={a.id}
              aspiration={a}
              onEdit={() => { setEditing(a); setEditorOpen(true); }}
              onDelete={() => deleteMutation.mutate(a.id)}
              deleting={deleteMutation.isPending && deleteMutation.variables === a.id}
            />
          ))}
        </div>
      )}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <AspirationEditorDialog existing={editing} onClose={() => setEditorOpen(false)} />
      </Dialog>
    </div>
  );
}

function AspirationRing({
  greenPct,
  combinedPct,
  done,
  quantified,
  aspirationId,
}: {
  greenPct: number;
  combinedPct: number;
  done: boolean;
  quantified: boolean;
  aspirationId: string;
}) {
  const r = 30;
  const size = 80;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;

  if (!quantified) {
    return (
      <div
        className="shrink-0 w-20 h-20 rounded-full border-2 border-dashed border-muted-foreground/25 flex items-center justify-center"
        data-testid={`ring-aspiration-${aspirationId}`}
      >
        <Target className="h-7 w-7 text-muted-foreground/40" />
      </div>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      className="shrink-0"
      data-testid={`ring-aspiration-${aspirationId}`}
    >
      <circle
        cx={cx} cy={cy} r={r}
        fill="none" strokeWidth="5"
        stroke="currentColor" className="text-muted-foreground/15"
      />
      {combinedPct > greenPct && (
        <circle
          cx={cx} cy={cy} r={r}
          fill="none" strokeWidth="5"
          stroke="#fbbf24"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - combinedPct / 100)}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      )}
      <circle
        cx={cx} cy={cy} r={r}
        fill="none" strokeWidth="5"
        stroke={done ? "#16a34a" : "#22c55e"}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - greenPct / 100)}
        transform={`rotate(-90 ${cx} ${cy})`}
      />
      {done ? (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
          fontSize="10" fontWeight="700" fill="#16a34a">Done!</text>
      ) : (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
          fontSize="13" fontWeight="700" fill="currentColor"
          style={{ fontVariantNumeric: "tabular-nums" }}>
          {Math.round(greenPct)}%
        </text>
      )}
    </svg>
  );
}

function AspirationCardView({
  aspiration: a,
  onEdit,
  onDelete,
  deleting,
}: {
  aspiration: AspirationCard;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const target = a.target ?? 0;
  const quantified = target > 0;
  const current = a.current ?? 0;
  const assumed = a.assumed ?? 0;
  const greenPct = quantified ? Math.max(0, Math.min(100, (current / target) * 100)) : 0;
  const combinedPct = quantified ? Math.max(0, Math.min(100, ((current + assumed) / target) * 100)) : 0;
  const done = quantified && greenPct >= 100;
  const unit = a.unit ? ` ${a.unit}` : "";
  const hrs = a.committedHoursPerWeek ?? a.recommendedHoursPerWeek;
  const deadline = a.targetDate
    ? new Date(`${a.targetDate.slice(0, 10)}T00:00:00`).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
    : null;
  const [planOpen, setPlanOpen] = useState(false);

  const domainIsPersonal = a.domain === "personal";

  return (
    <Card
      className="overflow-hidden transition-shadow hover:shadow-md dark:hover:shadow-black/30"
      data-testid={`card-aspiration-${a.id}`}
    >
      <CardContent className="p-4 space-y-3">
        {/* Top row: domain pill + action buttons */}
        <div className="flex items-center justify-between gap-2">
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${
              domainIsPersonal
                ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${domainIsPersonal ? "bg-violet-500" : "bg-blue-500"}`} />
            {a.domain}
          </span>
          <div className="flex items-center gap-0.5 -mr-1.5">
            <Button
              variant="ghost" size="icon" className="h-7 w-7"
              onClick={onEdit}
              data-testid={`button-edit-aspiration-${a.id}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost" size="icon" className="h-7 w-7"
              onClick={onDelete} disabled={deleting}
              data-testid={`button-delete-aspiration-${a.id}`}
            >
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>

        {/* Ring + title/why */}
        <div className="flex items-center gap-4">
          <AspirationRing
            greenPct={greenPct}
            combinedPct={combinedPct}
            done={done}
            quantified={quantified}
            aspirationId={a.id}
          />
          <div className="min-w-0 flex-1">
            <p
              className="font-semibold text-base leading-snug line-clamp-3"
              data-testid={`text-aspiration-title-${a.id}`}
            >
              {a.title}
            </p>
            {a.why && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{a.why}</p>
            )}
            {quantified && (
              <p
                className="text-xs text-muted-foreground mt-1.5 tabular-nums"
                data-testid={`text-aspiration-progress-${a.id}`}
              >
                {current.toLocaleString()} / {target.toLocaleString()}{unit}
                {assumed > 0 && (
                  <span className="text-yellow-600 dark:text-yellow-400">
                    {" "}+{assumed.toLocaleString()} assumed
                  </span>
                )}
              </p>
            )}
            {!quantified && (
              <p className="text-xs text-muted-foreground/60 italic mt-1">
                Add a target to track progress.
              </p>
            )}
          </div>
        </div>

        {/* Bottom metadata */}
        {(deadline || (hrs && Number(hrs) > 0) || a.autoScheduleEnabled) && (
          <div className="flex items-center gap-3 flex-wrap pt-2 border-t text-xs text-muted-foreground">
            {deadline && (
              <span
                className="inline-flex items-center gap-1"
                data-testid={`text-aspiration-deadline-${a.id}`}
              >
                <CalendarClock className="h-3 w-3" /> {deadline}
              </span>
            )}
            {hrs && Number(hrs) > 0 && (
              <span
                className="inline-flex items-center gap-1"
                data-testid={`text-aspiration-hours-${a.id}`}
              >
                <Clock className="h-3 w-3" /> {Number(hrs)} hrs/wk
              </span>
            )}
            {a.autoScheduleEnabled && (
              <Badge
                variant="outline"
                className="gap-1 text-xs h-5 border-violet-400 text-violet-700 dark:text-violet-300 px-1.5"
              >
                <Sparkles className="h-3 w-3" /> Auto
              </Badge>
            )}
          </div>
        )}

        {/* AI plan of action — written once, when this aspiration was first
            scheduled. Collapsed by default so a long roadmap doesn't bury the
            progress bar, which is what people open this panel for. */}
        {a.planOfAction && (
          <div className="pt-2 border-t">
            <button
              type="button"
              onClick={() => setPlanOpen((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-medium text-violet-700 dark:text-violet-300 hover:underline"
              data-testid={`button-aspiration-plan-${a.id}`}
            >
              <FileText className="h-3.5 w-3.5" />
              {planOpen ? "Hide your plan" : "View your plan"}
              <ChevronRight
                className={cn("h-3.5 w-3.5 transition-transform", planOpen && "rotate-90")}
              />
            </button>
            {planOpen && (
              <div
                className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed"
                data-testid={`text-aspiration-plan-${a.id}`}
              >
                {a.planOfAction}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AspirationEditorDialog({
  existing,
  onClose,
}: {
  existing: AspirationCard | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const isEdit = !!existing;

  const [title, setTitle] = useState(existing?.title ?? "");
  const [why, setWhy] = useState(existing?.why ?? "");
  const [domain, setDomain] = useState<string>(existing?.domain ?? "business");
  const [targetValue, setTargetValue] = useState(existing?.target != null ? String(existing.target) : "");
  const [unit, setUnit] = useState(existing?.unit ?? "");
  const [ratePerHour, setRatePerHour] = useState(existing?.ratePerHour != null ? String(Number(existing.ratePerHour)) : "");
  const [hoursPerWeek, setHoursPerWeek] = useState(
    existing?.committedHoursPerWeek != null
      ? String(Number(existing.committedHoursPerWeek))
      : (existing?.recommendedHoursPerWeek != null ? String(Number(existing.recommendedHoursPerWeek)) : ""),
  );
  const [targetDate, setTargetDate] = useState(existing?.targetDate ? existing.targetDate.slice(0, 10) : "");
  const [preferredDays, setPreferredDays] = useState<number[]>(existing?.preferredDays ?? []);
  const [preferredHour, setPreferredHour] = useState<string>(
    existing?.preferredHourOfDay != null ? String(existing.preferredHourOfDay) : "",
  );
  const [autoSchedule, setAutoSchedule] = useState<boolean>(existing?.autoScheduleEnabled ?? true);

  const toggleDay = (d: number) =>
    setPreferredDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = {
        title: title.trim(),
        why: why.trim() || null,
        domain,
        unit: unit.trim() || null,
        targetValue: targetValue.trim() === "" ? null : String(Number(targetValue)),
        ratePerHour: ratePerHour.trim() === "" ? null : String(Number(ratePerHour)),
        committedHoursPerWeek: hoursPerWeek.trim() === "" ? null : String(Number(hoursPerWeek)),
        targetDate: targetDate || null,
        preferredDays: preferredDays.length > 0 ? preferredDays : null,
        preferredHourOfDay: preferredHour.trim() === "" ? null : Number(preferredHour),
        autoScheduleEnabled: autoSchedule,
      };
      const saved = isEdit
        ? await apiRequest("PATCH", `/api/coach/aspirations/${existing!.id}`, body)
        : await apiRequest("POST", "/api/coach/aspirations", body);
      const row = await saved.json();
      // Re-run the runway when auto-schedule is on and we have a deadline + hours.
      // The aspiration is already saved at this point — a scheduling failure must
      // NOT roll it back or read as a failed save. Surface it as a soft warning.
      let scheduleError: string | null = null;
      if (autoSchedule && targetDate && Number(hoursPerWeek) > 0) {
        try {
          await apiRequest("POST", `/api/coach/aspirations/${row.id}/schedule`, {});
        } catch (e: any) {
          scheduleError = e?.message || "Auto-scheduling didn't run.";
        }
      }
      return { row, scheduleError };
    },
    onSuccess: ({ scheduleError }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/coach/aspirations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar-blocks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/calendar/events"] });
      if (scheduleError) {
        toast({
          title: isEdit ? "Aspiration updated" : "Aspiration created",
          description: `Saved, but auto-scheduling didn't run: ${scheduleError}`,
        });
      } else {
        toast({ title: isEdit ? "Aspiration updated" : "Aspiration created" });
      }
      onClose();
    },
    onError: (err: any) => {
      toast({
        title: "Couldn't save aspiration",
        description: err?.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });

  const canSave = title.trim().length > 0 && !saveMutation.isPending;
  // The first schedule of an aspiration runs a deep-reasoning AI pass that
  // writes the roadmap and picks the working rhythm. It takes several seconds,
  // so say what's happening instead of showing a bare spinner.
  const willGeneratePlan =
    autoSchedule &&
    !!targetDate &&
    Number(hoursPerWeek) > 0 &&
    !existing?.planOfAction;

  return (
    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="dialog-aspiration-editor">
      <DialogHeader>
        <DialogTitle>{isEdit ? "Edit aspiration" : "New aspiration"}</DialogTitle>
        <DialogDescription>
          Set a measurable target and a deadline, and your coach will fill the calendar with focus blocks toward it.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="asp-title">Aspiration</Label>
          <Input id="asp-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Finish writing my album" data-testid="input-aspiration-title" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="asp-why">Why it matters (optional)</Label>
          <Textarea id="asp-why" value={why} onChange={(e: any) => setWhy(e.target.value)} rows={2} data-testid="input-aspiration-why" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Domain</Label>
            <Select value={domain} onValueChange={setDomain}>
              <SelectTrigger data-testid="select-aspiration-domain"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="business">Business</SelectItem>
                <SelectItem value="personal">Personal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="asp-deadline">Deadline</Label>
            <Input id="asp-deadline" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} data-testid="input-aspiration-deadline" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="asp-target">Target amount</Label>
            <Input id="asp-target" type="number" inputMode="decimal" value={targetValue} onChange={(e) => setTargetValue(e.target.value)} placeholder="120" data-testid="input-aspiration-target" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="asp-unit">Unit</Label>
            <Input id="asp-unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="pages, songs, $" data-testid="input-aspiration-unit" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="asp-rate">Your pace (per hour)</Label>
            <Input id="asp-rate" type="number" inputMode="decimal" value={ratePerHour} onChange={(e) => setRatePerHour(e.target.value)} placeholder="2" data-testid="input-aspiration-rate" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="asp-hpw">Hours / week</Label>
            <Input id="asp-hpw" type="number" inputMode="decimal" value={hoursPerWeek} onChange={(e) => setHoursPerWeek(e.target.value)} placeholder="5" data-testid="input-aspiration-hpw" />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Ideal days (optional)</Label>
          <div className="flex gap-1">
            {WEEKDAY_LABELS.map((lbl, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => toggleDay(idx)}
                className={cn(
                  "h-8 w-8 rounded-md border text-xs font-medium transition-colors",
                  preferredDays.includes(idx)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted/40 text-muted-foreground border-border hover:bg-muted",
                )}
                data-testid={`toggle-aspiration-day-${idx}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="asp-hour">Ideal time of day (optional)</Label>
          <Select value={preferredHour || "__any__"} onValueChange={(v) => setPreferredHour(v === "__any__" ? "" : v)}>
            <SelectTrigger id="asp-hour" data-testid="select-aspiration-hour"><SelectValue placeholder="Any time" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any time</SelectItem>
              {Array.from({ length: 24 }, (_, h) => (
                <SelectItem key={h} value={String(h)}>
                  {new Date(2000, 0, 1, h).toLocaleTimeString([], { hour: "numeric" })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="toggle-aspiration-autoschedule">
          <input type="checkbox" className="h-4 w-4 accent-primary" checked={autoSchedule} onChange={(e) => setAutoSchedule(e.target.checked)} />
          <span className="inline-flex items-center gap-1"><Wand2 className="h-3.5 w-3.5" /> Auto-schedule focus blocks toward this</span>
        </label>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid="button-aspiration-cancel">Cancel</Button>
        <Button onClick={() => saveMutation.mutate()} disabled={!canSave} data-testid="button-aspiration-save">
          {saveMutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {willGeneratePlan ? "Building your plan…" : "Saving…"}
            </span>
          ) : (isEdit ? "Save & re-plan" : "Create & schedule")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
function BlockEditorDialog({
  mode,
  existing,
  defaultStart,
  onClose,
}: {
  mode: "create" | "edit";
  existing?: PersonalCalendarBlock;
  defaultStart?: Date;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const initStart = existing
    ? new Date(existing.startAt as unknown as string)
    : (defaultStart ?? new Date());
  const initEnd = existing
    ? new Date(existing.endAt as unknown as string)
    : new Date(initStart.getTime() + 60 * 60 * 1000);

  const [title, setTitle] = useState(existing?.title ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [startAt, setStartAt] = useState(toLocalInputValue(initStart));
  const [endAt, setEndAt] = useState(toLocalInputValue(initEnd));
  const [layer, setLayer] = useState<CalendarBlockLayer>((existing?.layer as CalendarBlockLayer) ?? "fixed");
  const [kind, setKind] = useState<CalendarBlockKind>((existing?.kind as CalendarBlockKind) ?? "user_fixed");
  const [visibility, setVisibility] = useState<CalendarBlockVisibility>((existing?.visibility as CalendarBlockVisibility) ?? "private");
  const [locationAddress, setLocationAddress] = useState(existing?.locationAddress ?? "");
  const [isRecurring, setIsRecurring] = useState<boolean>((existing as any)?.isRecurring ?? false);
  const [recurringFrequency, setRecurringFrequency] = useState<string>((existing as any)?.recurringFrequency ?? "weekly");
  const [recurringUntil, setRecurringUntil] = useState<string>((existing as any)?.recurringUntil ?? "");
  // Weekday-set for weekly/biweekly "on selected days" (0=Sun … 6=Sat). When
  // editing, seed from the saved set; otherwise default to the start weekday.
  const [recurringDays, setRecurringDays] = useState<number[]>(() => {
    const saved = (existing as any)?.recurringDaysOfWeek as number[] | null | undefined;
    if (Array.isArray(saved) && saved.length > 0) return [...saved].sort((a, b) => a - b);
    return [initStart.getDay()];
  });
  // Whether the user has manually edited the weekday selection. Until they do,
  // the default selected weekday tracks the block's Start date — so changing
  // Start (or enabling recurrence later) always pre-selects the right day
  // instead of a stale one captured at mount. Seed `true` when editing a block
  // that already has a saved set so we never clobber the user's saved choice.
  const userTouchedDaysRef = useRef<boolean>(
    Array.isArray((existing as any)?.recurringDaysOfWeek) &&
      ((existing as any)?.recurringDaysOfWeek as number[]).length > 0,
  );
  const showWeekdayPicker = recurringFrequency === "weekly" || recurringFrequency === "biweekly";
  const toggleRecurringDay = (d: number) => {
    userTouchedDaysRef.current = true;
    setRecurringDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b),
    );
  };
  // Keep the default selected weekday in sync with the chosen Start date until
  // the user manually picks days. Runs when Start changes or recurrence turns on.
  useEffect(() => {
    if (userTouchedDaysRef.current) return;
    if (!startAt) return;
    const wd = new Date(startAt).getDay();
    if (Number.isNaN(wd)) return;
    setRecurringDays([wd]);
  }, [startAt, isRecurring]);
  const [invitedMemberIds, setInvitedMemberIds] = useState<string[]>([]);

  const { data: calMembers = [] } = useQuery<any[]>({ queryKey: ["/api/calendar/members"] });
  const startDateIso = startAt ? (() => { try { return new Date(startAt).toISOString(); } catch { return null; } })() : null;
  const { data: hoursCheck = [] } = useQuery<any[]>({
    queryKey: ["/api/calendar/working-hours-check", { userIds: invitedMemberIds, isoDateTime: startDateIso }],
    queryFn: async () => {
      if (!startDateIso || invitedMemberIds.length === 0) return [];
      const res = await fetch("/api/calendar/working-hours-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userIds: invitedMemberIds, isoDateTime: startDateIso }),
      });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: invitedMemberIds.length > 0 && !!startDateIso,
  });

  const submit = useMutation({
    mutationFn: async () => {
      const payload: Record<string, any> = {
        title: title.trim(),
        description: description.trim() || null,
        startAt: new Date(startAt).toISOString(),
        endAt: new Date(endAt).toISOString(),
        layer,
        kind,
        visibility,
        locationAddress: locationAddress.trim() || null,
        isRecurring,
        recurringFrequency: isRecurring ? recurringFrequency : null,
        recurringUntil: isRecurring && recurringUntil ? recurringUntil : null,
        recurringDaysOfWeek:
          isRecurring && (recurringFrequency === "weekly" || recurringFrequency === "biweekly") && recurringDays.length > 0
            ? [...recurringDays].sort((a, b) => a - b)
            : null,
      };
      if (mode === "edit" && existing) {
        return apiRequest("PATCH", `/api/calendar-blocks/${existing.id}`, payload);
      }
      return apiRequest("POST", `/api/calendar-blocks`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar-blocks"] });
      toast({ title: mode === "edit" ? "Block updated" : "Block added" });
      onClose();
    },
    onError: (err: any) => {
      toast({ title: "Couldn't save the block", description: err?.message, variant: "destructive" });
    },
  });

  const titleInvalid = !title.trim();
  const timeInvalid = !startAt || !endAt || new Date(endAt) <= new Date(startAt);
  const recurringInvalid = isRecurring && showWeekdayPicker && recurringDays.length === 0;

  return (
    <DialogContent className="sm:max-w-lg" data-testid="dialog-block-editor">
      <DialogHeader>
        <DialogTitle>{mode === "edit" ? "Edit block" : "Add a time block"}</DialogTitle>
        <DialogDescription>
          Block out anything immovable so the calendar coach knows to plan around it.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <Label htmlFor="block-title">Title</Label>
          <Input
            id="block-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Doctor appointment"
            data-testid="input-block-title"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="block-start">Start</Label>
            <Input
              id="block-start"
              type="datetime-local"
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
              data-testid="input-block-start"
            />
          </div>
          <div>
            <Label htmlFor="block-end">End</Label>
            <Input
              id="block-end"
              type="datetime-local"
              value={endAt}
              onChange={(e) => setEndAt(e.target.value)}
              data-testid="input-block-end"
            />
          </div>
        </div>

        {/* Recurring — sits directly under Start/End so it reads as part of
            the time setup. Weekly/biweekly reveal a weekday picker. */}
        <div className="space-y-3 rounded-md border border-border/60 p-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="block-recurring"
              checked={isRecurring}
              onChange={(e) => setIsRecurring(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-primary"
              data-testid="checkbox-block-recurring"
            />
            <Label htmlFor="block-recurring" className="cursor-pointer">Recurring</Label>
          </div>

          {isRecurring && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Frequency</Label>
                  <Select value={recurringFrequency} onValueChange={setRecurringFrequency}>
                    <SelectTrigger data-testid="select-block-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Biweekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="block-until">Until (optional)</Label>
                  <Input
                    id="block-until"
                    type="date"
                    value={recurringUntil}
                    onChange={(e) => setRecurringUntil(e.target.value)}
                    data-testid="input-block-until"
                  />
                </div>
              </div>

              {showWeekdayPicker && (
                <div>
                  <Label className="text-sm">Repeat on</Label>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label, idx) => {
                      const selected = recurringDays.includes(idx);
                      return (
                        <button
                          key={idx}
                          type="button"
                          data-testid={`btn-recurring-day-${idx}`}
                          aria-pressed={selected}
                          onClick={() => toggleRecurringDay(idx)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${selected ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary hover:text-foreground"}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {recurringDays.length === 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Pick at least one day to repeat on.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Teammate awareness — invitee picker + working hours warning */}
        {calMembers.length > 0 && (
          <div>
            <Label className="text-sm">Invite teammates</Label>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {calMembers.map((m: any) => {
                const uid = m.id ?? m.userId;
                const name = [m.firstName, m.lastName].filter(Boolean).join(" ") || m.email || uid;
                const selected = invitedMemberIds.includes(uid);
                return (
                  <button
                    key={uid}
                    type="button"
                    data-testid={`btn-invite-member-${uid}`}
                    onClick={() => setInvitedMemberIds((prev) => selected ? prev.filter((id) => id !== uid) : [...prev, uid])}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${selected ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary hover:text-foreground"}`}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Working-hours warning + TZ labels for invited teammates */}
        {hoursCheck.length > 0 && (
          <div className="space-y-2">
            {hoursCheck.filter((u: any) => !u.withinHours).length > 0 && (
              <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-amber-800 dark:text-amber-300">Outside working hours</p>
                  <ul className="mt-0.5 space-y-0.5">
                    {hoursCheck.filter((u: any) => !u.withinHours).map((u: any) => {
                      const m = calMembers.find((cm: any) => (cm.id ?? cm.userId) === u.userId);
                      const displayName = m ? [m.firstName, m.lastName].filter(Boolean).join(" ") || u.userId : u.userId;
                      return (
                        <li key={u.userId} className="text-xs text-amber-700 dark:text-amber-400">
                          {displayName} — {u.userLocalTime} <span className="opacity-70">({u.timezone})</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}
            {hoursCheck.filter((u: any) => u.withinHours && u.timezone !== Intl.DateTimeFormat().resolvedOptions().timeZone).length > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                {hoursCheck.filter((u: any) => u.withinHours && u.timezone !== Intl.DateTimeFormat().resolvedOptions().timeZone).map((u: any) => {
                  const m = calMembers.find((cm: any) => (cm.id ?? cm.userId) === u.userId);
                  const displayName = m ? [m.firstName, m.lastName].filter(Boolean).join(" ") || u.userId : u.userId;
                  return <span key={u.userId}>{displayName}: {u.userLocalTime} ({u.timezone})</span>;
                })}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Layer</Label>
            <Select value={layer} onValueChange={(v) => setLayer(v as CalendarBlockLayer)}>
              <SelectTrigger data-testid="select-block-layer"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixed — never moved</SelectItem>
                <SelectItem value="fluid">Fluid — can reshuffle</SelectItem>
                <SelectItem value="life">Life — protected default</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Kind</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as CalendarBlockKind)}>
              <SelectTrigger data-testid="select-block-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {KIND_OPTIONS.map((k) => (
                  <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Who can see this</Label>
          <Select value={visibility} onValueChange={(v) => setVisibility(v as CalendarBlockVisibility)}>
            <SelectTrigger data-testid="select-block-visibility"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="private">Private — only me (others see 'Busy')</SelectItem>
              <SelectItem value="role">My role — same-role teammates</SelectItem>
              <SelectItem value="org">Everyone in the workspace</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">{VISIBILITY_LABEL[visibility].description}</p>
        </div>

        <div>
          <Label htmlFor="block-location">Location (optional)</Label>
          <Input
            id="block-location"
            value={locationAddress}
            onChange={(e) => setLocationAddress(e.target.value)}
            placeholder="123 Main St, Nashville TN"
            data-testid="input-block-location"
          />
        </div>

        <div>
          <Label htmlFor="block-description">Notes (optional)</Label>
          <Textarea
            id="block-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            data-testid="input-block-description"
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => submit.mutate()}
          disabled={titleInvalid || timeInvalid || recurringInvalid || submit.isPending}
          data-testid="button-save-block"
        >
          {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {mode === "edit" ? "Save changes" : "Add block"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

// ============================================================================
// Calendar Hub Slice 3 — Lodging panel
// ============================================================================
// Per-stay record of where the artist is sleeping while on tour. The drive
// cascade reads these to override the "previous show's city" origin so a
// hotel-night-between-cities is modeled correctly.

const LODGING_SOURCE_LABEL: Record<string, { label: string; className: string }> = {
  manual:       { label: "Manual",        className: "bg-muted text-muted-foreground" },
  gmail:        { label: "Gmail",         className: "bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200" },
  ical:         { label: "iCal",          className: "bg-purple-100 text-purple-900 dark:bg-purple-900/40 dark:text-purple-200" },
  "apple-mail": { label: "Apple Mail",    className: "bg-purple-100 text-purple-900 dark:bg-purple-900/40 dark:text-purple-200" },
};

function fmtStayDate(iso: string): string {
  // YYYY-MM-DD → "Mon, Jan 5"
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}

function nightsBetween(checkIn: string, checkOut: string): number {
  const a = new Date(checkIn).getTime();
  const b = new Date(checkOut).getTime();
  return Math.max(1, Math.round((b - a) / 86_400_000));
}

// ============================================================================
// Meeting Requests
// ============================================================================
function MeetingRequestsView() {
  const { toast } = useToast();
  const [sendOpen, setSendOpen] = useState(false);

  // Received requests
  const { data: received = [], isLoading: recvLoading } = useQuery<CalendarMeetingRequest[]>({
    queryKey: ["/api/meeting-requests", "received"],
    queryFn: async () => {
      const res = await fetch("/api/meeting-requests?role=recipient");
      if (!res.ok) return [];
      return res.json();
    },
  });

  // Sent requests
  const { data: sent = [], isLoading: sentLoading } = useQuery<CalendarMeetingRequest[]>({
    queryKey: ["/api/meeting-requests", "sent"],
    queryFn: async () => {
      const res = await fetch("/api/meeting-requests?role=requester");
      if (!res.ok) return [];
      return res.json();
    },
  });

  const pendingReceived = received.filter((r) => r.status === "pending");
  const pastReceived = received.filter((r) => r.status !== "pending");

  const respondMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "accepted" | "declined" }) => {
      await apiRequest("POST", `/api/meeting-requests/${id}/respond`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meeting-requests"] });
      toast({ title: "Response sent" });
    },
    onError: () => toast({ title: "Failed to respond", variant: "destructive" }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Meeting Requests</h3>
          <p className="text-sm text-muted-foreground">Request a time with a teammate or respond to incoming requests.</p>
        </div>
        <Dialog open={sendOpen} onOpenChange={setSendOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="button-send-meeting-request">
              <UserPlus className="h-4 w-4" />
              Request meeting
            </Button>
          </DialogTrigger>
          <SendMeetingRequestDialog onClose={() => setSendOpen(false)} />
        </Dialog>
      </div>

      {/* Pending incoming */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          Received
          {pendingReceived.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">{pendingReceived.length} pending</Badge>
          )}
        </h4>
        {recvLoading && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
        {!recvLoading && received.length === 0 && (
          <p className="text-sm text-muted-foreground italic">No meeting requests yet.</p>
        )}
        {pendingReceived.map((req) => (
          <MeetingRequestCard key={req.id} req={req} direction="received" onRespond={(status) => respondMutation.mutate({ id: req.id, status })} responding={respondMutation.isPending} />
        ))}
        {pastReceived.map((req) => (
          <MeetingRequestCard key={req.id} req={req} direction="received" />
        ))}
      </div>

      {/* Sent */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          Sent
        </h4>
        {sentLoading && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
        {!sentLoading && sent.length === 0 && (
          <p className="text-sm text-muted-foreground italic">You haven't sent any meeting requests.</p>
        )}
        {sent.map((req) => (
          <MeetingRequestCard key={req.id} req={req} direction="sent" />
        ))}
      </div>
    </div>
  );
}

const STATUS_STYLE: Record<string, string> = {
  pending:  "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  accepted: "bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-200",
  declined: "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200",
};

function MeetingRequestCard({
  req,
  direction,
  onRespond,
  responding,
}: {
  req: CalendarMeetingRequest;
  direction: "received" | "sent";
  onRespond?: (status: "accepted" | "declined") => void;
  responding?: boolean;
}) {
  const start = new Date(req.proposedStart);
  const end = new Date(req.proposedEnd);
  const fmtRange = `${start.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })} · ${start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} – ${end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  const other = direction === "received" ? (req.requesterName ?? req.requesterId) : (req.recipientName ?? req.recipientId);

  return (
    <div className="flex items-start gap-3 rounded-md border p-3 hover-elevate" data-testid={`card-meeting-request-${req.id}`}>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate">{req.title}</span>
          <Badge variant="outline" className={cn("text-xs capitalize", STATUS_STYLE[req.status])}>{req.status}</Badge>
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="h-3 w-3" /> {fmtRange}
        </div>
        <div className="text-xs text-muted-foreground">
          {direction === "received" ? "From: " : "To: "}<span className="font-medium text-foreground">{other}</span>
        </div>
        {req.message && <div className="text-xs text-muted-foreground italic">"{req.message}"</div>}
      </div>
      {direction === "received" && req.status === "pending" && onRespond && (
        <div className="flex flex-col gap-1 shrink-0">
          <Button size="sm" variant="outline" className="gap-1 text-green-700 border-green-400 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-950" disabled={responding} onClick={() => onRespond("accepted")} data-testid={`button-accept-request-${req.id}`}>
            <CheckCircle2 className="h-3.5 w-3.5" /> Accept
          </Button>
          <Button size="sm" variant="outline" className="gap-1 text-red-700 border-red-400 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950" disabled={responding} onClick={() => onRespond("declined")} data-testid={`button-decline-request-${req.id}`}>
            <XCircle className="h-3.5 w-3.5" /> Decline
          </Button>
        </div>
      )}
    </div>
  );
}

function SendMeetingRequestDialog({ onClose }: { onClose: () => void }) {
  const { toast } = useToast();
  const [recipientId, setRecipientId] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const tomorrow = new Date(Date.now() + 86_400_000);
  const [startAt, setStartAt] = useState(() => {
    const d = new Date(tomorrow);
    d.setHours(10, 0, 0, 0);
    return toLocalInputValue(d);
  });
  const [endAt, setEndAt] = useState(() => {
    const d = new Date(tomorrow);
    d.setHours(11, 0, 0, 0);
    return toLocalInputValue(d);
  });

  const { data: members = [] } = useQuery<CalendarMember[]>({
    queryKey: ["/api/calendar/members"],
    queryFn: async () => {
      const res = await fetch("/api/calendar/members");
      if (!res.ok) return [];
      return res.json();
    },
  });

  const send = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/meeting-requests", {
        recipientId,
        title: title.trim(),
        message: message.trim() || null,
        proposedStart: new Date(startAt).toISOString(),
        proposedEnd: new Date(endAt).toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meeting-requests"] });
      toast({ title: "Meeting request sent" });
      onClose();
    },
    onError: () => toast({ title: "Failed to send request", variant: "destructive" }),
  });

  const invalid = !recipientId || !title.trim() || !startAt || !endAt || new Date(startAt) >= new Date(endAt);

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Request a meeting</DialogTitle>
        <DialogDescription>Propose a time to meet with a teammate. They'll receive a notification to accept or decline.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div>
          <Label>Teammate</Label>
          <Select value={recipientId} onValueChange={setRecipientId}>
            <SelectTrigger data-testid="select-meeting-recipient"><SelectValue placeholder="Select teammate…" /></SelectTrigger>
            <SelectContent>
              {members.map((m) => (
                <SelectItem key={m.userId} value={m.userId}>{m.name || m.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="meeting-title">Subject</Label>
          <Input id="meeting-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Quick sync" data-testid="input-meeting-title" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="meeting-start">Start</Label>
            <Input id="meeting-start" type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="input-meeting-start" />
          </div>
          <div>
            <Label htmlFor="meeting-end">End</Label>
            <Input id="meeting-end" type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} data-testid="input-meeting-end" />
          </div>
        </div>
        <div>
          <Label htmlFor="meeting-message">Message (optional)</Label>
          <Textarea id="meeting-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder="What's this about?" data-testid="input-meeting-message" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={() => send.mutate()} disabled={invalid || send.isPending} data-testid="button-submit-meeting-request">
          {send.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Send request
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function LodgingView() {
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);

  // Pull a generous window — past 30d through next 365d. Lodging is far
  // lower volume than schedule entries, so the simpler fetch beats the
  // complexity of a per-week pager.
  const from = new Date(Date.now() - 30 * 86_400_000);
  const to = new Date(Date.now() + 365 * 86_400_000);
  const fromIso = from.toISOString();
  const toIso = to.toISOString();

  const { data: stays = [], isLoading } = useQuery<LodgingStay[]>({
    queryKey: ["/api/lodging-stays", { from: fromIso, to: toIso }],
    queryFn: async () => {
      const params = new URLSearchParams({ from: fromIso, to: toIso });
      const res = await fetch(`/api/lodging-stays?${params}`);
      if (!res.ok) throw new Error("Failed to load lodging stays");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/lodging-stays/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lodging-stays"] });
      toast({ title: "Stay removed", description: "Drive times for nearby shows are being recalculated." });
    },
    onError: () => {
      toast({ title: "Couldn't remove the stay", variant: "destructive" });
    },
  });

  // Slice 4 — pending hotel-confirmation suggestions auto-extracted from Gmail.
  // Same queryKey as the parent badge so a single fetch powers both.
  const { data: pendingSuggestions = [] } = useQuery<LodgingSuggestion[]>({
    queryKey: ["/api/lodging-suggestions", { status: "pending" }],
    queryFn: async () => {
      const res = await fetch("/api/lodging-suggestions?status=pending");
      if (!res.ok) return [];
      return res.json();
    },
  });
  const [reviewing, setReviewing] = useState<LodgingSuggestion | null>(null);
  const rejectMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/lodging-suggestions/${id}/reject`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lodging-suggestions"] });
      toast({ title: "Suggestion dismissed" });
    },
    onError: () => {
      toast({ title: "Couldn't dismiss the suggestion", variant: "destructive" });
    },
  });

  // Task #244 — pull hotel/reservation events straight from the connected
  // calendar (calendar.readonly) into the same review queue. This is how
  // lodging gets auto-detected without the restricted gmail.readonly scope.
  const scanCalendarMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/lodging-suggestions/scan-calendar");
      return (await res.json()) as { created: number; matched: number; duplicates: number };
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["/api/lodging-suggestions"] });
      toast({
        title: r.created > 0 ? `Found ${r.created} new stay${r.created === 1 ? "" : "s"}` : "No new stays found",
        description:
          r.created > 0
            ? "Review them below before they affect drive times."
            : r.matched > 0
              ? "Everything in your calendar is already in your review queue."
              : "No hotel or reservation events found in your calendar.",
      });
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't scan your calendar",
        description: /not connected/i.test(e?.message || "")
          ? "Connect Google Calendar first, then try again."
          : undefined,
        variant: "destructive",
      });
    },
  });

  // Group: upcoming (checkOut >= today) vs past.
  const todayIso = new Date().toISOString().slice(0, 10);
  const upcoming = stays.filter(s => s.checkOutDate >= todayIso);
  const past = stays.filter(s => s.checkOutDate < todayIso);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Where you're sleeping</h3>
          <p className="text-sm text-muted-foreground">
            Add hotels and home stays so the calendar coach knows the right starting point for each drive.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => scanCalendarMutation.mutate()}
            disabled={scanCalendarMutation.isPending}
            data-testid="button-scan-calendar-lodging"
          >
            {scanCalendarMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Calendar className="h-4 w-4" />
            )}
            Scan calendar
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="button-add-lodging">
                <Plus className="h-4 w-4" />
                Add stay
              </Button>
            </DialogTrigger>
            <LodgingEditorDialog mode="create" onClose={() => setCreateOpen(false)} />
          </Dialog>
        </div>
      </div>

      {pendingSuggestions.length > 0 && (
        <Card className="border-amber-300 dark:border-amber-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BedDouble className="h-4 w-4 text-amber-600" />
              Hotel confirmations to review
              <Badge variant="secondary" data-testid="badge-suggestions-count">
                {pendingSuggestions.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingSuggestions.map((s) => (
              <div
                key={s.id}
                className="flex items-start justify-between gap-3 rounded-md border bg-card/40 p-3"
                data-testid={`row-lodging-suggestion-${s.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium" data-testid={`text-suggestion-hotel-${s.id}`}>
                      {s.hotelName || "Hotel stay"}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {Math.round((s.confidence ?? 0) * 100)}% confidence
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground mt-0.5">
                    {s.checkInDate} → {s.checkOutDate}
                    {s.city ? ` · ${s.city}${s.state ? `, ${s.state}` : ""}` : s.address ? ` · ${s.address}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => setReviewing(s)}
                    data-testid={`button-review-suggestion-${s.id}`}
                  >
                    Review
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => rejectMutation.mutate(s.id)}
                    disabled={rejectMutation.isPending && rejectMutation.variables === s.id}
                    data-testid={`button-reject-suggestion-${s.id}`}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {reviewing && (
        <Dialog open onOpenChange={(open) => { if (!open) setReviewing(null); }}>
          <LodgingEditorDialog
            mode="approve-suggestion"
            suggestion={reviewing}
            onClose={() => setReviewing(null)}
          />
        </Dialog>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Upcoming stays</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : upcoming.length === 0 ? (
            <div className="text-sm text-muted-foreground italic pl-1" data-testid="text-no-upcoming-lodging">
              No upcoming stays. Add one and the cascade will pick it up automatically.
            </div>
          ) : (
            upcoming.map((s) => (
              <LodgingRow
                key={s.id}
                stay={s}
                onDelete={() => deleteMutation.mutate(s.id)}
                deleting={deleteMutation.isPending && deleteMutation.variables === s.id}
              />
            ))
          )}
        </CardContent>
      </Card>

      {past.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-muted-foreground">Past stays</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {past.map((s) => (
              <LodgingRow
                key={s.id}
                stay={s}
                onDelete={() => deleteMutation.mutate(s.id)}
                deleting={deleteMutation.isPending && deleteMutation.variables === s.id}
                muted
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function LodgingRow({
  stay,
  onDelete,
  deleting,
  muted,
}: {
  stay: LodgingStay;
  onDelete: () => void;
  deleting: boolean;
  muted?: boolean;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const sourceStyle = LODGING_SOURCE_LABEL[stay.source] ?? LODGING_SOURCE_LABEL.manual;
  const nights = nightsBetween(stay.checkInDate, stay.checkOutDate);
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-md border p-3 hover-elevate",
        muted && "opacity-70",
      )}
      data-testid={`row-lodging-${stay.id}`}
    >
      <BedDouble className="h-5 w-5 mt-0.5 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate" data-testid={`text-lodging-name-${stay.id}`}>
            {stay.name || stay.address}
          </span>
          <Badge variant="outline" className={cn("text-xs", sourceStyle.className)} data-testid={`badge-lodging-source-${stay.id}`}>
            {sourceStyle.label}
          </Badge>
        </div>
        <div className="mt-1 text-xs text-muted-foreground tabular-nums" data-testid={`text-lodging-dates-${stay.id}`}>
          {fmtStayDate(stay.checkInDate)} → {fmtStayDate(stay.checkOutDate)} · {nights} {nights === 1 ? "night" : "nights"}
        </div>
        <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
          <MapPin className="h-3 w-3" />
          <span className="truncate">
            {stay.city && stay.state ? `${stay.city}, ${stay.state}` : stay.address}
          </span>
        </div>
        {stay.notes && (
          <div className="mt-1 text-sm text-muted-foreground whitespace-pre-line">{stay.notes}</div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon" data-testid={`button-edit-lodging-${stay.id}`}>
              <Pencil className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <LodgingEditorDialog mode="edit" existing={stay} onClose={() => setEditOpen(false)} />
        </Dialog>
        <Button
          variant="ghost"
          size="icon"
          onClick={onDelete}
          disabled={deleting}
          data-testid={`button-delete-lodging-${stay.id}`}
        >
          {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

function LodgingEditorDialog({
  mode,
  existing,
  suggestion,
  onClose,
}: {
  mode: "create" | "edit" | "approve-suggestion";
  existing?: LodgingStay;
  // Slice 4 — when mode === "approve-suggestion", pre-fill from this row
  // and submit to the approve endpoint instead of plain create.
  suggestion?: LodgingSuggestion;
  onClose: () => void;
}) {
  const { toast } = useToast();
  // Default check-in to today, checkout to tomorrow — the most common
  // "I'm adding a hotel real quick" flow.
  const todayIso = new Date().toISOString().slice(0, 10);
  const tomorrowIso = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);

  const seed = mode === "approve-suggestion" ? suggestion : existing;
  const [checkInDate, setCheckInDate] = useState(
    seed?.checkInDate ?? todayIso,
  );
  const [checkOutDate, setCheckOutDate] = useState(
    seed?.checkOutDate ?? tomorrowIso,
  );
  const [name, setName] = useState(
    (mode === "approve-suggestion" ? suggestion?.hotelName : existing?.name) ?? "",
  );
  const [address, setAddress] = useState(seed?.address ?? "");
  const [city, setCity] = useState(seed?.city ?? "");
  const [state, setState] = useState(seed?.state ?? "");
  const [notes, setNotes] = useState(existing?.notes ?? "");

  const submit = useMutation({
    mutationFn: async () => {
      const payload = {
        checkInDate,
        checkOutDate,
        name: name.trim() || null,
        address: address.trim(),
        city: city.trim() || null,
        state: state.trim().toUpperCase() || null,
        notes: notes.trim() || null,
      };
      if (mode === "edit" && existing) {
        return apiRequest("PATCH", `/api/lodging-stays/${existing.id}`, payload);
      }
      if (mode === "approve-suggestion" && suggestion) {
        // Approve copies the (possibly user-edited) fields onto a real
        // lodging_stay row server-side and triggers cascade re-emit.
        return apiRequest(
          "POST",
          `/api/lodging-suggestions/${suggestion.id}/approve`,
          payload,
        );
      }
      return apiRequest("POST", `/api/lodging-stays`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lodging-stays"] });
      if (mode === "approve-suggestion") {
        queryClient.invalidateQueries({ queryKey: ["/api/lodging-suggestions"] });
      }
      toast({
        title:
          mode === "edit"
            ? "Stay updated"
            : mode === "approve-suggestion"
            ? "Suggestion approved"
            : "Stay added",
        description: "Drive times for affected shows are being recalculated.",
      });
      onClose();
    },
    onError: (err: any) => {
      toast({ title: "Couldn't save the stay", description: err?.message, variant: "destructive" });
    },
  });

  const addressInvalid = !address.trim();
  const datesInvalid = !checkInDate || !checkOutDate || checkOutDate <= checkInDate;

  return (
    <DialogContent className="sm:max-w-lg" data-testid="dialog-lodging-editor">
      <DialogHeader>
        <DialogTitle>
          {mode === "edit"
            ? "Edit stay"
            : mode === "approve-suggestion"
            ? "Review hotel from email"
            : "Add a lodging stay"}
        </DialogTitle>
        <DialogDescription>
          {mode === "approve-suggestion"
            ? "We pulled this from a hotel confirmation in your inbox. Edit anything that looks off, then approve to save it."
            : "The cascade uses the city/state to pick the right drive origin for the morning after each night."}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <Label htmlFor="lodging-name">Hotel / place name (optional)</Label>
          <Input
            id="lodging-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Hampton Inn Downtown"
            data-testid="input-lodging-name"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="lodging-checkin">Check-in</Label>
            <Input
              id="lodging-checkin"
              type="date"
              value={checkInDate}
              onChange={(e) => setCheckInDate(e.target.value)}
              data-testid="input-lodging-checkin"
            />
          </div>
          <div>
            <Label htmlFor="lodging-checkout">Check-out</Label>
            <Input
              id="lodging-checkout"
              type="date"
              value={checkOutDate}
              onChange={(e) => setCheckOutDate(e.target.value)}
              data-testid="input-lodging-checkout"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="lodging-address">Address</Label>
          <Input
            id="lodging-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="123 Main St, Austin TX 78701"
            data-testid="input-lodging-address"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label htmlFor="lodging-city">City</Label>
            <Input
              id="lodging-city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Austin"
              data-testid="input-lodging-city"
            />
          </div>
          <div>
            <Label htmlFor="lodging-state">State</Label>
            <Input
              id="lodging-state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="TX"
              maxLength={4}
              data-testid="input-lodging-state"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="lodging-notes">Notes (optional)</Label>
          <Textarea
            id="lodging-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            data-testid="input-lodging-notes"
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => submit.mutate()}
          disabled={addressInvalid || datesInvalid || submit.isPending}
          data-testid="button-save-lodging"
        >
          {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {mode === "edit"
            ? "Save changes"
            : mode === "approve-suggestion"
            ? "Approve & save"
            : "Add stay"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
